import type { GradientChartMarker } from '@repo/ui/components/gradient-chart';

import type { InvestorPerformance } from '@/lib/investor-analytics';
import type { InvestorTransaction } from '../types/api';
import { formatCompactUsd } from '@/lib/format/usd/compact-usd';
import { dayOf } from '@/lib/investor-analytics';

const MAX_MARKERS = 5;

/** Every line is drawn as the growth of this many dollars. */
export const GROWTH_BASE = 100;

interface GrowthPoint {
  /** The day, `YYYY-MM-DD`, whatever precision the API stamped. */
  date: string;
  /** Null on a day the index could not be computed: the line breaks there. */
  portfolio: number | null;
  [benchmarkKey: `bench_${string}`]: number;
}

export const benchmarkKey = (symbol: string) => `bench_${symbol}` as const;

/**
 * Growth of 100, with the portfolio line time weighted so investor deposits
 * and withdrawals do not move it. That is what makes the benchmark overlay
 * a fair comparison: both lines answer "what did 100 do", and neither jumps
 * because money arrived.
 *
 * The index is the backend's (BE-000A, API 1.58.0) — the same series its
 * scalar metrics are computed from, so the chart and the returns table
 * beside it can no longer disagree. It used to be derived here from the
 * value series and a locally assembled flow ledger, which was a stand-in
 * with two defects: it needed every flow page loaded to be trustworthy, and
 * it applied flows at the start of a period where the backend applies them
 * at the end. Those conventions give different numbers on the same data.
 *
 * A null level means the sub-period could not be computed. The backend is
 * explicit that null breaks the line on purpose, so the day is kept with no
 * level. The chart's X axis is categorical: leaving the day out would put
 * its neighbours side by side and draw the line straight across the gap.
 * Only the days before the first level are dropped, where there is nothing
 * yet to break away from.
 *
 * The line is rebased so the first plotted level is 100. A window that
 * opens mid history is served at whatever level the index had reached, and
 * both the footnote ("every line starts at $100") and the tooltip (percent
 * from 100) need the first day shown to be 100 for either to be true.
 */
export function buildGrowthSeries(data: InvestorPerformance): GrowthPoint[] {
  const served = [...(data.portfolioIndex?.points ?? [])]
    .map((point) => ({ date: dayOf(point.timestamp), value: point.value }))
    .sort((left, right) => left.date.localeCompare(right.date));
  const firstLevelIndex = served.findIndex((point) => point.value != null);
  if (firstLevelIndex === -1) return [];

  const plotted = served.slice(firstLevelIndex);
  const firstLevel = plotted[0]!.value!;
  // An index level is positive. One that is not cannot be rebased, so the
  // series is drawn as served rather than divided by zero.
  const rebase = firstLevel > 0 ? GROWTH_BASE / firstLevel : 1;

  const points = new Map<string, GrowthPoint>();
  for (const point of plotted) {
    points.set(point.date, {
      date: point.date,
      portfolio: point.value == null ? null : point.value * rebase
    });
  }
  const ordered = [...points.values()];

  // Each benchmark is rebased onto the portfolio line itself: normalized by
  // its last print on the first portfolio day the index has printed at all,
  // then scaled to the portfolio's indexed level that day. The overlay
  // departs FROM the line and tracks the index's relative moves — correct
  // regardless of the units or anchor the backend used; a partially covered
  // index simply starts later, on the line.
  //
  // Indexes only print on trading days while the portfolio has every
  // calendar day, so each portfolio day carries the index's most recent
  // print forward — an index's level on a Saturday IS Friday's close.
  // Without this, every weekend, holiday, and provider gap snapped the
  // overlay into disconnected islands. A day the portfolio line breaks
  // still carries the benchmark: only the portfolio could not be computed.
  for (const benchmark of data.benchmarks) {
    const sorted = benchmark.series
      .map((point) => ({ date: dayOf(point.timestamp), value: point.value }))
      .sort((left, right) => left.date.localeCompare(right.date));
    let cursor = 0;
    let lastPrint: number | null = null;
    let anchorPrint: number | null = null;
    let anchorLevel: number | null = null;
    for (const point of ordered) {
      while (cursor < sorted.length && sorted[cursor]!.date <= point.date) {
        if (sorted[cursor]!.value > 0) lastPrint = sorted[cursor]!.value;
        cursor += 1;
      }
      if (lastPrint == null) continue;
      if (anchorPrint == null || anchorLevel == null) {
        // The anchor needs a portfolio level to depart from.
        if (point.portfolio == null) continue;
        anchorPrint = lastPrint;
        anchorLevel = point.portfolio;
      }
      point[benchmarkKey(benchmark.symbol)] =
        (lastPrint / anchorPrint) * anchorLevel;
    }
  }

  return ordered;
}

interface FlowCluster {
  dayIndex: number;
  date: string;
  portfolio: number;
  net: number;
  deposits: number;
  withdrawals: number;
}

function clusterLabel(cluster: FlowCluster): string {
  const amount = formatCompactUsd(Math.abs(cluster.net), {
    maximumFractionDigits: 1
  });
  const count = cluster.deposits + cluster.withdrawals;
  // Kept short on purpose: these sit inline on the plot, so every extra word
  // is width that collides with the neighbouring marker.
  if (count === 1) return `${cluster.net >= 0 ? '+' : '−'}${amount}`;
  if (cluster.withdrawals === 0) return `+${amount} · ${count}`;
  if (cluster.deposits === 0) return `−${amount} · ${count}`;
  return `${cluster.net >= 0 ? '+' : '−'}${amount} net · ${count}`;
}

export function buildFlowMarkers(
  growth: GrowthPoint[],
  transactions: InvestorTransaction[] | undefined
): GradientChartMarker[] {
  if (!transactions || growth.length === 0) return [];
  const first = growth[0]!.date;
  const last = growth.at(-1)!.date;
  const indexByDate = new Map(
    growth.map((point, index) => [point.date, index])
  );

  const flows = transactions
    .filter(
      (transaction) =>
        (transaction.type === 'Deposit' || transaction.type === 'Withdraw') &&
        dayOf(transaction.date) >= first &&
        dayOf(transaction.date) <= last
    )
    .sort((left, right) => left.date.localeCompare(right.date));
  // Newest first, levelled days only: a marker needs a height to sit at,
  // and the day of a large flow is exactly the kind the index cannot
  // compute.
  const levelled = growth
    .filter(
      (entry): entry is GrowthPoint & { portfolio: number } =>
        entry.portfolio != null
    )
    .reverse();

  // Labels render at ~10px type and are centred on their point, so each one
  // claims roughly a seventh of the axis. Anything closer than that overlaps
  // its neighbour into unreadable text (measured on a 140-day window, where
  // an 8% gap still collided), so nearby flows merge into one netted cluster.
  const minGap = Math.max(1, Math.ceil(growth.length * 0.15));
  const clusters: FlowCluster[] = [];
  for (const flow of flows) {
    const day = dayOf(flow.date);
    // Snap to the nearest charted day at or before the flow.
    const point = levelled.find((entry) => entry.date <= day);
    if (!point) continue;
    const dayIndex = indexByDate.get(point.date)!;
    const open = clusters.at(-1);
    if (open && dayIndex - open.dayIndex < minGap) {
      open.net += flow.amount;
      if (flow.amount >= 0) open.deposits += 1;
      else open.withdrawals += 1;
      continue;
    }
    clusters.push({
      dayIndex,
      date: point.date,
      portfolio: point.portfolio,
      net: flow.amount,
      deposits: flow.amount >= 0 ? 1 : 0,
      withdrawals: flow.amount >= 0 ? 0 : 1
    });
  }

  const lastIndex = growth.length - 1;
  return clusters.slice(-MAX_MARKERS).map((cluster) => ({
    x: cluster.date,
    y: cluster.portfolio,
    label: clusterLabel(cluster),
    // A centered label on an edge point spills into the axis gutter.
    labelPosition:
      cluster.dayIndex / Math.max(lastIndex, 1) < 0.1
        ? 'right'
        : cluster.dayIndex / Math.max(lastIndex, 1) > 0.9
          ? 'left'
          : 'top'
  }));
}
